"use client";

import { enGB, fr } from "date-fns/locale";
import { AnimatePresence, motion } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

import {
  Calendar,
  type CalendarRange,
  type CalendarView,
} from "@/components/calendar";
import { DEFAULT_BOOKING_RULES } from "@/lib/booking/slots";

import { getBookingSlots } from "../_actions/booking.action";

import { BookingForm } from "./booking-form";

// =============================================================================

type Slot = { start: Date; end: Date };

const EASE = [0.16, 1, 0.3, 1] as const;

const subscribeNever = () => () => {};

/** Hour of the visitor's clock when it is `hour` o'clock in Paris */
function parisHourToLocal(hour: number): number {
  const now = new Date();
  const parisHour = Number(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/Paris",
      hour: "numeric",
      hourCycle: "h23",
    }).format(now)
  );
  return (hour + now.getHours() - parisHour + 24) % 24;
}

/**
 * Grid drawn while the slots load: the default rules (Paris hours, open
 * days), so the calendar keeps the same size once they arrive
 */
function defaultGrid() {
  const minHour = parisHourToLocal(DEFAULT_BOOKING_RULES.startHour);
  const maxHour = parisHourToLocal(DEFAULT_BOOKING_RULES.endHour);
  return maxHour > minHour ? { minHour, maxHour } : { minHour: 8, maxHour: 19 };
}

const DEFAULT_HIDDEN_WEEKDAYS = [0, 1, 2, 3, 4, 5, 6].filter(
  (day) => !DEFAULT_BOOKING_RULES.weekdays.includes(day)
);

/** "2026-10-12" in the visitor's time zone */
const localDay = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/**
 * Free hours of the visible days are the free slots; everything else of the
 * grid (booked, busy, closed, past) is hatched the same way: nothing tells
 * why a time is not offered.
 */
function closedRanges(slots: Slot[], minHour: number, maxHour: number) {
  const byDay = new Map<string, Slot[]>();
  for (const slot of slots) {
    const day = localDay(slot.start);
    byDay.set(day, [...(byDay.get(day) ?? []), slot]);
  }
  if (slots.length === 0) return [];

  const first = new Date(slots[0].start);
  first.setHours(0, 0, 0, 0);
  const last = new Date(slots[slots.length - 1].start);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = today < first ? today : first;

  const ranges: CalendarRange[] = [];
  for (
    const day = new Date(start);
    day <= last;
    day.setDate(day.getDate() + 1)
  ) {
    const open = new Date(day);
    open.setHours(minHour, 0, 0, 0);
    const close = new Date(day);
    close.setHours(maxHour, 0, 0, 0);
    let cursor = open;
    for (const slot of byDay.get(localDay(day)) ?? []) {
      if (slot.start > cursor) ranges.push({ start: cursor, end: slot.start });
      if (slot.end > cursor) cursor = slot.end;
    }
    if (cursor < close) ranges.push({ start: cursor, end: close });
  }
  return ranges;
}

/**
 * "Book a call" tab: the free slots in a week or month calendar (in the
 * visitor's time zone), then the request form for the chosen slot.
 */
export function BookingPanel() {
  const t = useTranslations("Connect.booking");
  const locale = useLocale();
  const [status, setStatus] = useState<"loading" | "ready" | "closed">(
    "loading"
  );
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selected, setSelected] = useState<Slot | null>(null);
  const [view, setView] = useState<CalendarView>("week");
  const [date, setDate] = useState(() => new Date());
  // The browser's own zone, unknown on the server (hydration safe)
  const timeZone = useSyncExternalStore(
    subscribeNever,
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    () => ""
  );

  const load = useCallback(async () => {
    try {
      const result = await getBookingSlots();
      if (!result.enabled) {
        setStatus("closed");
        return;
      }
      const next = result.slots.map((slot) => ({
        start: new Date(slot.start),
        end: new Date(slot.end),
      }));
      setSlots(next);
      setStatus("ready");
      // Open on the week of the first free slot
      if (next[0])
        setDate((current) =>
          current < next[0].start ? next[0].start : current
        );
    } catch {
      setStatus("closed");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Grid window: the free hours in the visitor's time zone
  const { minHour, maxHour } = useMemo(() => {
    if (slots.length === 0) return defaultGrid();
    const starts = slots.map((slot) => slot.start.getHours());
    const ends = slots.map(
      (slot) => slot.end.getHours() + (slot.end.getMinutes() > 0 ? 1 : 0)
    );
    return {
      minHour: Math.max(0, Math.min(...starts)),
      maxHour: Math.min(24, Math.max(...ends.map((hour) => hour || 24))),
    };
  }, [slots]);

  const disabledRanges = useMemo(
    () => closedRanges(slots, minHour, maxHour),
    [slots, minHour, maxHour]
  );
  const slotsByDay = useMemo(() => {
    const counts = new Map<string, number>();
    for (const slot of slots) {
      const day = localDay(slot.start);
      counts.set(day, (counts.get(day) ?? 0) + 1);
    }
    return counts;
  }, [slots]);
  const hiddenWeekdays = useMemo(() => {
    if (slots.length === 0) return DEFAULT_HIDDEN_WEEKDAYS;
    const open = new Set(slots.map((slot) => slot.start.getDay()));
    return [0, 1, 2, 3, 4, 5, 6].filter((day) => !open.has(day));
  }, [slots]);

  const dateLocale = locale === "fr" ? fr : enGB;
  const slotLabel = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });

  const pick = (range: CalendarRange) => {
    const slot = slots.find(
      (item) => item.start.getTime() === range.start.getTime()
    );
    if (slot) setSelected(slot);
  };

  return (
    <div>
      <p className="max-w-2xl text-[clamp(1.0625rem,1.4vw,1.25rem)] leading-relaxed text-muted-foreground">
        {status === "closed" ? t("closed") : t("intro")}
      </p>

      {status !== "closed" && (
        <div className="mt-[clamp(2rem,4vw,3rem)]">
          <AnimatePresence mode="wait" initial={false}>
            {selected ? (
              <motion.div
                key="form"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.45, ease: EASE }}
              >
                <BookingForm
                  slot={selected}
                  timeZone={timeZone}
                  onBack={() => setSelected(null)}
                  onBooked={() => {
                    setSelected(null);
                    void load();
                  }}
                />
              </motion.div>
            ) : (
              <motion.div
                key="calendar"
                initial={{ opacity: 0, x: -24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 24 }}
                transition={{ duration: 0.45, ease: EASE }}
                aria-busy={status === "loading"}
                className="relative"
              >
                <Calendar
                  view={view}
                  onViewChange={setView}
                  date={date}
                  onDateChange={setDate}
                  locale={dateLocale}
                  labels={{
                    today: t("calendar.today"),
                    previous: t("calendar.previous"),
                    next: t("calendar.next"),
                    week: t("calendar.week"),
                    month: t("calendar.month"),
                    more: (count) => t("calendar.more", { count }),
                    slot: (start) =>
                      t("calendar.slot", { date: slotLabel.format(start) }),
                  }}
                  weekStartsOn={1}
                  hiddenWeekdays={hiddenWeekdays}
                  minHour={minHour}
                  maxHour={maxHour}
                  slotDuration={30}
                  disabledRanges={disabledRanges}
                  onSlotClick={pick}
                  onDateClick={(day) => {
                    setDate(day);
                    setView("week");
                  }}
                  isDayDisabled={(day) => !slotsByDay.has(localDay(day))}
                  renderDayAddon={(day) => {
                    const count = slotsByDay.get(localDay(day));
                    return count ? (
                      <span className="text-[0.7rem] text-muted-foreground">
                        <span className="sm:hidden">{count}</span>
                        <span className="hidden sm:inline">
                          {t("slots", { count })}
                        </span>
                      </span>
                    ) : null;
                  }}
                  className="max-h-[42rem]"
                />
                {status === "loading" && (
                  <div className="absolute inset-0 grid place-items-center rounded-xl bg-background/60 text-sm text-muted-foreground backdrop-blur-[2px]">
                    {t("loading")}
                  </div>
                )}
                {/* Its line is kept while the time zone is unknown */}
                <p className="mt-4 min-h-5 text-sm text-muted-foreground">
                  {status === "ready" && slots.length === 0
                    ? t("noSlots")
                    : timeZone && t("timezone", { zone: timeZone })}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

// =============================================================================
